import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';

const UploadResourcePage = () => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'audio',
    tags: '',
    file: null
  });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);

  const categoryInfo = {
    audio: { label: '🎵 Audio Resources', maxSize: 20, icon: '🎵' },
    podcast: { label: '🎧 Podcasts (PDF)', maxSize: 10, icon: '🎧' },
    activity: { label: '📝 Activities (PDF)', maxSize: 10, icon: '📝' }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Get file extension
    const ext = file.name.split('.').pop().toLowerCase();
    
    // Map extension to category
    const categoryMap = {
      mp3: 'audio', wav: 'audio', m4a: 'audio',
      pdf: 'podcast', 
      doc: 'podcast', docx: 'podcast',
      xlsx: 'activity', xls: 'activity'
    };

    const detectedCategory = categoryMap[ext] || formData.category;
    const maxSize = categoryInfo[detectedCategory].maxSize * 1024 * 1024;

    if (file.size > maxSize) {
      setError(`File size exceeds ${categoryInfo[detectedCategory].maxSize}MB limit`);
      return;
    }

    setError('');
    setFormData({...formData, file, category: detectedCategory});

    // Create preview for images/videos
    if (['image', 'video'].includes(detectedCategory)) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview({ type: detectedCategory, src: e.target.result });
      reader.readAsDataURL(file);
    } else {
      setPreview({ type: 'file', name: file.name, size: (file.size / 1024 / 1024).toFixed(2) });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Please enter resource title');
      return;
    }
    if (!formData.description.trim()) {
      setError('Please enter resource description');
      return;
    }
    if (!formData.file) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    
    // Simulate upload
    setTimeout(() => {
      setSuccess(true);
      setUploading(false);
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setFormData({ title: '', description: '', category: 'pdf', tags: '', file: null });
        setPreview(null);
        setSuccess(false);
        fileInputRef.current.value = '';
      }, 2000);
    }, 1500);
  };

  return (
    <>
      <DashboardSidebar userRole="physiotherapist" />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '40px 20px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <Link to="/physio-dashboard" style={{ color: '#667eea', textDecoration: 'none', marginBottom: '20px', display: 'inline-block', fontWeight: 500 }}>
            ← Back to Dashboard
          </Link>

          <div style={{ marginBottom: '30px' }}>
            <h1 style={{ fontSize: '32px', color: '#333', margin: '20px 0 10px 0', fontWeight: 700 }}>📚 Upload Learning Resource</h1>
            <p style={{ color: '#666', marginBottom: '10px' }}>Share audio, podcast, and activity resources with your clients</p>
            <p style={{ color: '#999', fontSize: '14px' }}>Resources will be reviewed and approved before becoming visible to clients</p>
          </div>

          {success && (
            <div style={{
              background: '#d1fae5',
              border: '1px solid #6ee7b7',
              borderRadius: '8px',
              padding: '15px 20px',
              marginBottom: '20px',
              color: '#065f46',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              ✓ Resource uploaded successfully! It will be reviewed before publishing.
            </div>
          )}

          {error && (
            <div style={{
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              padding: '15px 20px',
              marginBottom: '20px',
              color: '#991b1b'
            }}>
              ⚠️ {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ background: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                Resource Title <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Stress Management Techniques"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                Description <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                placeholder="Describe what this resource covers and who it's for..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  minHeight: '120px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                {formData.description.length} / 500 characters
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                Resource Category <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              >
                {Object.entries(categoryInfo).map(([key, {label}]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                Max file size: {categoryInfo[formData.category].maxSize}MB
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                Tags (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g., anxiety, stress, meditation (comma-separated)"
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: 600, color: '#333' }}>
                Upload File <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div
                style={{
                  border: '2px dashed #ddd',
                  borderRadius: '8px',
                  padding: '30px',
                  textAlign: 'center',
                  background: '#fafafa',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.background = '#f3f4ff';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#ddd';
                  e.currentTarget.style.background = '#fafafa';
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>📁</div>
                <p style={{ margin: '0 0 5px 0', fontWeight: 500, color: '#333' }}>Click to upload or drag and drop</p>
                <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>
                  Supported formats depend on category (max {categoryInfo[formData.category].maxSize}MB)
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept={formData.category === 'audio' ? '.mp3,.wav,.m4a' : formData.category === 'podcast' ? '.pdf,.doc,.docx' : '.xlsx,.xls'}
              />

              {preview && (
                <div style={{ marginTop: '15px', padding: '15px', background: '#f9fafb', borderRadius: '8px' }}>
                  {preview.type === 'image' && (
                    <div>
                      <img
                        src={preview.src}
                        alt="Preview"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '200px',
                          borderRadius: '6px',
                          marginBottom: '10px'
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Image preview</p>
                    </div>
                  )}
                  {preview.type === 'video' && (
                    <div>
                      <video
                        src={preview.src}
                        style={{
                          maxWidth: '300px',
                          maxHeight: '200px',
                          borderRadius: '6px',
                          marginBottom: '10px'
                        }}
                        controls
                      />
                      <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Video preview</p>
                    </div>
                  )}
                  {preview.type === 'file' && (
                    <div style={{ fontSize: '12px' }}>
                      <p style={{ margin: '0 0 5px 0', fontWeight: 500, color: '#333' }}>
                        📎 {preview.name}
                      </p>
                      <p style={{ margin: 0, color: '#666' }}>
                        {preview.size}MB
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                disabled={uploading}
                style={{
                  padding: '12px 30px',
                  background: uploading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  fontSize: '14px'
                }}
                onMouseOver={(e) => {
                  if (!uploading) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {uploading ? '⏳ Uploading...' : '📤 Upload Resource'}
              </button>
              <Link
                to="/physio-dashboard"
                style={{
                  padding: '12px 30px',
                  background: '#e5e7eb',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontSize: '14px'
                }}
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UploadResourcePage;
